// La ruta de la pantalla dedicada y la GEOMETRÍA de la franja del panel.
//
// EL ANCHO VIVE ACÁ Y EN NINGÚN OTRO LADO. Lo consumen dos lugares que tienen
// que coincidir al pixel:
//   • el propio panel (`PanelChatAtlas`), que se dibuja de ese ancho;
//   • el offset del panel de contenido (`right` en `DashboardLayout`), que es
//     lo que hace que el chat EMPUJE la pantalla en vez de taparla.
// Si fueran dos números distintos, el contenido quedaría con una franja de
// fondo a la vista o, peor, medio tapado por el chat. Los dos leen esta misma
// función a través de `useEmpujeChat`.

// `/agente` desde ADR-2026-09-12-el-agente-toma-el-diseno-del-asistente-v2 (el Chat
// pasó a llamarse Agente); `/chat` sigue vivo como alias en `App.tsx`.
export const RUTA_CHAT_ATLAS = '/agente';

/**
 * Marca que viaja en el `state` de la navegación cuando a `/chat` se llega
 * EXPANDIENDO el panel (botón de maximizar), y no por la sidebar ni por la URL.
 *
 * Los dos caminos terminan en la misma pantalla pero significan cosas
 * distintas: expandir conserva la conversación Y su contexto de apertura ("me
 * abriste desde el Mapa"), mientras que entrar en frío no tiene ningún "acá"
 * del que hablar. Sin esta marca, el efecto de montaje de la pantalla borraba
 * el contexto SIEMPRE —el agente dejaba de saber qué era "esto"— y además
 * dejaba el panel abierto para todas las demás pantallas.
 *
 * Es un `state` de navegación y no un query param a propósito: no ensucia una
 * URL que el usuario puede copiar y compartir, y pegarla a mano cae en el
 * camino frío, que es lo correcto.
 */
export const ESTADO_VENGO_DEL_PANEL = 'chatAtlasVengoDelPanel';

/** ¿Este `location.state` dice que venimos de expandir el panel? */
export const vieneDelPanel = (estado: unknown): boolean =>
  !!estado &&
  typeof estado === 'object' &&
  (estado as Record<string, unknown>)[ESTADO_VENGO_DEL_PANEL] === true;

/** Ancho cómodo de la franja, cuando la ventana da para eso. 600 y no 420: en el Canva del
 *  Asistente de Crear las cards de generación quedaban apretadas
 *  (ADR-2026-10-13-el-panel-del-agente-mas-ancho y -2). */
export const ANCHO_PANEL_CHAT = 600;


/** Piso de la franja: más angosta que esto el chat deja de ser legible
 *  (las burbujas quedan de dos palabras por línea). */
export const ANCHO_MINIMO_PANEL = 300;

/** Lo que se le reserva SIEMPRE a la pantalla de atrás. Como el chat empuja en
 *  vez de tapar, sin esta reserva una ventana chica dejaría al contenido con
 *  100px y la pantalla de atrás sería inusable — que es justo lo que el modo
 *  "empujar" quiere evitar. */
export const ANCHO_MINIMO_CONTENIDO = 380;

/**
 * Ancho de la columna del chat cuando el LIENZO está a la vista en `/agente`
 * (ADR-2026-09-16-con-el-lienzo-la-biblioteca-se-abre-encima-del-chat). Era un poco más
 * ancha que la franja del panel; desde que el panel mide 600 ya no
 * (ADR-2026-10-13-el-panel-del-agente-mas-ancho-2). Es también el ancho de la biblioteca en ese estado:
 * los dos miden lo mismo para que la biblioteca se abra ENCIMA del chat y lo tape justo,
 * en vez de empujar al lienzo.
 */
export const FRACCION_COLUMNA_CON_LIENZO = 0.34;
// Ya no es más ancha que el panel (600): es la columna de `/agente`, que se queda como estaba
// (ADR-2026-10-13-el-panel-del-agente-mas-ancho-2).
export const ANCHO_MINIMO_COLUMNA_CON_LIENZO = 460;
export const ANCHO_MAXIMO_COLUMNA_CON_LIENZO = 580;

/**
 * Una fracción del contenedor con piso y techo, como la biblioteca sin lienzo
 * (`anchoDeBiblioteca`): un número fijo quedaba chico en una pantalla grande, donde la
 * biblioteca medía 640. El lienzo es "el contenido" y conserva su reserva
 * (`ANCHO_MINIMO_CONTENIDO`); por debajo de eso manda el piso del chat.
 */
export function anchoColumnaConLienzo(anchoContenedor: number): number {
  const proporcional = Math.round(anchoContenedor * FRACCION_COLUMNA_CON_LIENZO);
  const comodo = Math.min(
    ANCHO_MAXIMO_COLUMNA_CON_LIENZO,
    Math.max(ANCHO_MINIMO_COLUMNA_CON_LIENZO, proporcional),
  );
  const cabe = anchoContenedor - ANCHO_MINIMO_CONTENIDO;
  return Math.max(ANCHO_MINIMO_PANEL, Math.min(comodo, cabe));
}

/**
 * Ancho real de la franja para un viewport y una sidebar dados.
 *
 * Puro (y testeado) porque es el número del que dependen las dos mitades del
 * layout. La cuenta es: de lo que queda al costado de la sidebar, el chat toma
 * hasta `ANCHO_PANEL_CHAT`, pero nunca tanto como para dejar al contenido con
 * menos de `ANCHO_MINIMO_CONTENIDO`; y nunca menos de `ANCHO_MINIMO_PANEL`,
 * aunque para respetar ese piso haya que comerse la reserva del contenido.
 *
 * En una ventana de escritorio angosta (768px con la sidebar abierta) el piso
 * gana y al contenido le quedan ~200px: es el peor caso y se resuelve
 * plegando la sidebar, que es exactamente para lo que está el botón.
 */
export function anchoPanelChat(anchoVentana: number, anchoSidebar: number): number {
  const disponible = anchoVentana - anchoSidebar;
  const cabe = disponible - ANCHO_MINIMO_CONTENIDO;
  return Math.max(ANCHO_MINIMO_PANEL, Math.min(ANCHO_PANEL_CHAT, cabe));
}
